import { Outlet, useParams } from 'react-router-dom';
// material
import { Card, CardContent, Fade, Link } from '@mui/material';
import { styled } from '@mui/material/styles';
// components
import { BookingRouteParams, MerchantOutletContext } from '@/@type/router/booking';
import LoadingComponent from '@/components/LoadingComponent';
import { useFetchMerchant } from '@/components/public/booking/components/useFetchMerchant';
import { useSnackbarHelper } from '@/components/useSnackbarHelper';
import { Box, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

// ----------------------------------------------------------------------

const HeaderStyle = styled('header')(({ theme }) => ({
    top: 0,
    left: 0,
    lineHeight: 0,
    width: '100%',
    padding: theme.spacing(0),
    zIndex: 10,
    backgroundColor: theme.palette.background.default,
    [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(3, 3, 0)
    }
}));
// ----------------------------------------------------------------------

const BANNER_MIN_HEIGHT = 120;
const BANNER_MAX_HEIGHT = 320;
const BANNER_DEFAULT_HEIGHT = 150;

export default function MerchantOnlyLayout(): JSX.Element {
    const { merchantId } = useParams<BookingRouteParams>();
    const { showSnackbar } = useSnackbarHelper();
    const { merchant, bookingSettings, googleSettings, fetchMerchantLoading, fetchMerchantError } = useFetchMerchant(merchantId || '');
    const [logoImages, setLogoImages] = useState<string[]>([]);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [context, setContext] = useState<MerchantOutletContext>({ merchantId: merchantId || '' });
    const bannerContainerRef = useRef<HTMLDivElement | null>(null);
    const [bannerWidth, setBannerWidth] = useState<number>(0);
    const [imageDimensions, setImageDimensions] = useState<Record<number, { w: number; h: number }>>({});

    useEffect(() => {
        if (merchantId) {
            setContext((prev) => ({ ...prev, merchantId }));
        }
    }, [merchantId]);

    useEffect(() => {
        if (merchant) {
            setContext((prev) => ({ ...prev, merchant }));
        }
        if (merchant?.logoImages && merchant.logoImages.length > 0) {
            const filteredImages = merchant.logoImages.filter(
                (image): image is string => image !== null && image !== undefined && image !== ''
            );
            setLogoImages(filteredImages);
            setCurrentImageIndex(0);
            setImageDimensions({});
        }
    }, [merchant]);

    useEffect(() => {
        if (bookingSettings) {
            setContext((prev) => ({ ...prev, bookingSettings }));
        }
    }, [bookingSettings]);

    useEffect(() => {
        if (googleSettings) {
            setContext((prev) => ({ ...prev, googleSettings }));
        }
    }, [googleSettings]);

    useEffect(() => {
        if (fetchMerchantError) {
            showSnackbar('Merchant not found', 'error');
        }
    }, [fetchMerchantError, showSnackbar]);
    useEffect(() => {
        if (currentImageIndex >= logoImages.length && logoImages.length > 0) {
            setCurrentImageIndex(0);
        }
    }, [logoImages, currentImageIndex]);

    useEffect(() => {
        if (logoImages.length > 1) {
            const interval = setInterval(() => {
                setCurrentImageIndex((prevIndex) => (prevIndex + 1) % logoImages.length);
            }, 3000);
            return (): void => clearInterval(interval);
        }
    }, [logoImages.length]);

    useEffect(() => {
        const node = bannerContainerRef.current;
        if (!node) return;
        // Initial measurement
        setBannerWidth(node.getBoundingClientRect().width);
        if (typeof ResizeObserver === 'undefined') return;
        const observer = new ResizeObserver((entries) => {
            const entry = entries[0];
            if (entry) {
                setBannerWidth(entry.contentRect.width);
            }
        });
        observer.observe(node);
        return (): void => observer.disconnect();
    }, [logoImages.length]);

    const currentDim = imageDimensions[currentImageIndex];
    const targetBannerHeight = ((): number => {
        if (!currentDim || !bannerWidth) return BANNER_DEFAULT_HEIGHT;
        const ideal = bannerWidth * (currentDim.h / currentDim.w);
        return Math.min(BANNER_MAX_HEIGHT, Math.max(BANNER_MIN_HEIGHT, ideal));
    })();

    return (
        <>
            <HeaderStyle>
                {fetchMerchantLoading && <LoadingComponent />}
                {!fetchMerchantLoading && !fetchMerchantError && merchant && (
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        sx={{
                            maxWidth: { xs: '100%', sm: 800 },
                            mx: { xs: 0, sm: 'auto' },
                            width: '100%',
                            px: { xs: 0, sm: 2 }
                        }}
                    >
                        <Card
                            sx={{
                                width: '100%',
                                borderRadius: { xs: 0, sm: 1 },
                                boxShadow: 'none'
                            }}
                        >
                            <Box
                                ref={bannerContainerRef}
                                sx={{
                                    height: targetBannerHeight,
                                    transition: 'height 300ms ease',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    bgcolor: 'background.default'
                                }}
                            >
                                {logoImages.map((image, index) => (
                                    <Fade
                                        key={index}
                                        in={index === currentImageIndex}
                                        timeout={800}
                                        mountOnEnter
                                        unmountOnExit
                                        style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: '100%',
                                            height: '100%'
                                        }}
                                    >
                                        <Box sx={{ position: 'absolute', inset: 0 }}>
                                            {/* Blurred backdrop fills letterbox area aesthetically */}
                                            <Box
                                                component="img"
                                                src={image}
                                                alt=""
                                                aria-hidden
                                                sx={{
                                                    position: 'absolute',
                                                    inset: 0,
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover',
                                                    filter: 'blur(24px) brightness(0.9)',
                                                    transform: 'scale(1.1)'
                                                }}
                                            />
                                            {/* Foreground logo: contain so it is fully visible without distortion */}
                                            <Box
                                                component="img"
                                                src={image}
                                                alt="merchant logo"
                                                onLoad={(e): void => {
                                                    const img = e.currentTarget as HTMLImageElement;
                                                    const w = img.naturalWidth;
                                                    const h = img.naturalHeight;
                                                    if (!w || !h) return;
                                                    setImageDimensions((prev) => {
                                                        const existing = prev[index];
                                                        if (existing && existing.w === w && existing.h === h) {
                                                            return prev;
                                                        }
                                                        return { ...prev, [index]: { w, h } };
                                                    });
                                                }}
                                                sx={{
                                                    position: 'relative',
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'contain',
                                                    display: 'block'
                                                }}
                                            />
                                        </Box>
                                    </Fade>
                                ))}
                            </Box>
                            <CardContent
                                sx={{
                                    paddingX: 4,
                                    paddingTop: 1,
                                    '&:last-child': {
                                        paddingBottom: 1
                                    }
                                }}
                            >
                                <Typography variant="subtitle1" fontWeight="bold">
                                    {merchant.name}
                                </Typography>
                                {merchant.altName && (
                                    <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                        {merchant.altName}
                                    </Typography>
                                )}
                                {merchant.phoneNumber && (
                                    <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                        <Link href={`tel:${merchant?.phoneNumber}`} sx={{ color: 'inherit', textDecoration: 'underline' }}>
                                            📞 {merchant?.phoneNumber}
                                        </Link>
                                    </Typography>
                                )}
                                <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                    📍 {merchant.address}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Box>
                )}
            </HeaderStyle>

            <Box>
                <Outlet context={context} />
            </Box>
        </>
    );
}
